import type { CompanyInfoImportData } from '@/services/accountService/glInvoiceService';

/**
 * Build annual taxFileNo from GL Excel Company info fields.
 * Matches EpCompanyDetails BR auto-fill: "prefix/br" or bare "br".
 */
export function buildTaxFileNoFromGlImport(
    data: Pick<CompanyInfoImportData, 'br' | 'taxFileNoRaw' | 'taxFileNo'> | {
        br?: string | null;
        taxFileNoRaw?: string | null;
        taxFileNo?: string | null;
    } | null | undefined,
): string | null {
    if (!data) return null;
    if (data.taxFileNo) return data.taxFileNo;

    const br = (data.br || '').trim().replace(/-$/, '');
    const prefix = (data.taxFileNoRaw || '').trim();
    if (prefix && br) return `${prefix}/${br}`;
    if (br) return br;
    return null;
}

/** Human-readable list of missing required annual fields for stepper feedback. */
export function formatAnnualValidationMessage(errors: {
    periodFrom?: string;
    periodTo?: string;
    taxFileNo?: string;
    taxRep?: string;
}): string {
    const labels: string[] = [];
    if (errors.periodFrom) labels.push('Period From');
    if (errors.periodTo) labels.push('Period To');
    if (errors.taxFileNo) labels.push('Tax File No (稅檔編號)');
    if (errors.taxRep) labels.push('Tax Rep details');
    if (labels.length === 0) {
        return 'Please fill in all required fields before continuing.';
    }
    return `Please complete required fields: ${labels.join(', ')}. Scroll to Company Details (公司詳情) if Tax File No is missing.`;
}

/** Excel 公司名去掉中文后缀，用于与 DB 英文名匹配 */
export function extractEnglishCompanyName(name: string | null | undefined): string {
    if (!name) return '';
    return name.replace(/[\u4e00-\u9fff]+/g, '').trim();
}

export function companyNamesEquivalent(
    excelName: string | null | undefined,
    dbName: string | null | undefined,
): boolean {
    if (!excelName || !dbName) return false;
    const e = excelName.trim();
    const d = dbName.trim();
    if (e.toLowerCase() === d.toLowerCase()) return true;
    const eEng = extractEnglishCompanyName(e);
    if (eEng && eEng.toLowerCase() === d.toLowerCase()) return true;
    if (d && e.toLowerCase().startsWith(d.toLowerCase())) {
        const rest = e.slice(d.length);
        return rest.length === 0 || !/^[a-z0-9]/i.test(rest);
    }
    return false;
}

/** 从公司下拉列表中按 Excel 公司名匹配已有公司 */
export function matchCompanyFromImportName<T extends { companyName?: string | null }>(
    companies: T[],
    importCompanyName: string | null | undefined,
): T | null {
    if (!importCompanyName || companies.length === 0) return null;
    const exact = companies.find((c) => companyNamesEquivalent(importCompanyName, c.companyName));
    return exact ?? null;
}
